/**
 * R3's pools list. The server already filters this to the pools the viewer
 * and the viewed fan both belong to, so this component filters nothing.
 *
 * No per-pool status label: `/pool/shared-with-account` returns none, and
 * `resolvePoolStatusLabel` on `/pool/list-mine` is viewer-relative copy that
 * would say the wrong thing about the wrong person.
 *
 * An active pool's row links to /pools?poolId=; an archived one stays inert,
 * because pool/get refuses an archived pool (ISSUE-1028).
 */
import { cn, FOCUS_RING, Skeleton } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { PoolCardContent } from "../pools/PoolCardContent";

import {
  POOLS_UNAVAILABLE,
  SHARED_POOLS_EMPTY,
  SHARED_POOLS_TITLE,
} from "./fanProfileCopy";

export type typeSharedPoolRow = {
  bannerImageUrl?: string | null;
  creatorHandle?: string | null;
  contestType?: string;
  sportKey?: string;
  poolId: string;
  name: string;
  /**
   * The server-resolved league name, "NFL". NOT the leagueKey (ISSUE-851).
   * leagueKey is the ingest join key, "football-nfl", and a fan must never
   * read it. Absent from this type on purpose, so reintroducing the defect
   * is a type error rather than a review catch.
   */
  leagueLabel: string;
  poolStatus: "active" | "archived";
};

export type typeSharedPoolsListProps = {
  isLoading: boolean;
  hasFailed: boolean;
  rows: readonly typeSharedPoolRow[];
  testId?: string | undefined;
};

const SharedPoolRowContent = ({
  row,
}: {
  row: typeSharedPoolRow;
}): ReactNode => (
  <PoolCardContent
    name={row.name}
    contestType={row.contestType ?? ""}
    sport={row.sportKey}
    imageUrl={row.bannerImageUrl}
    byline={row.creatorHandle ? `@${row.creatorHandle}` : undefined}
    facts={[row.leagueLabel]}
  />
);

export const SharedPoolsList = (props: typeSharedPoolsListProps): ReactNode => (
  <section
    className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4"
    data-testid={props.testId}
  >
    <h2 className="text-h2 text-ink">{SHARED_POOLS_TITLE}</h2>
    {props.isLoading ? (
      <div className="flex flex-col gap-2" data-testid="fan-pools-loading">
        <Skeleton shape="text" />
        <Skeleton shape="text" />
        <Skeleton shape="text" />
      </div>
    ) : props.hasFailed ? (
      <p
        className="text-body text-ink-secondary"
        data-testid="fan-pools-unavailable"
      >
        {POOLS_UNAVAILABLE}
      </p>
    ) : props.rows.length === 0 ? (
      <p className="text-body text-ink-secondary" data-testid="fan-pools-empty">
        {SHARED_POOLS_EMPTY}
      </p>
    ) : (
      <ul
        className="flex flex-col divide-y divide-line"
        data-testid="fan-pools"
      >
        {props.rows.map((row) => (
          <li key={row.poolId} data-testid={`fan-pool-${row.poolId}`}>
            {row.poolStatus === "active" ? (
              <Link
                to="/pools"
                search={{ poolId: row.poolId }}
                data-testid={`fan-shared-pool-link-${row.poolId}`}
                className={cn(
                  "flex min-h-touch-target items-center gap-3 rounded-md p-3",
                  FOCUS_RING,
                )}
              >
                <SharedPoolRowContent row={row} />
              </Link>
            ) : (
              <div className="flex items-center gap-3 p-3">
                <SharedPoolRowContent row={row} />
              </div>
            )}
          </li>
        ))}
      </ul>
    )}
  </section>
);
